import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ScoreBadge, RiskBadge, SourceBadge } from "../components/StockBits";
import WhyThisPickDialog from "../components/WhyThisPickDialog";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { TrendingUp, TrendingDown, Target, Activity, Trophy, Calendar, ChevronRight, SlidersHorizontal, RotateCcw, Star } from "lucide-react";
import { cn } from "../lib/utils";
import { Skeleton } from "../components/ui/skeleton";

const DEFAULTS = { m: 40, t: 35, r: 25 };

function HeroStat({ icon: Icon, label, value, sub, tint }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-muted-foreground">
        <Icon className={cn("h-3.5 w-3.5", tint)} /> {label}
      </div>
      <div className="text-2xl sm:text-3xl font-bold font-mono">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function WeightRow({ label, testId, value, onChange, tint }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: tint }} /> {label}
        </span>
        <span className="font-mono text-sm font-semibold">{value}%</span>
      </div>
      <Slider data-testid={testId} value={[value]} min={0} max={100} step={5}
        onValueChange={(v) => onChange(v[0])} />
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [w, setW] = useState(DEFAULTS);
  const [showWeights, setShowWeights] = useState(false);
  const [watchlist, setWatchlist] = useState([]);

  const loadWatchlist = () => {
    if (!user) { setWatchlist([]); return; }
    api.get("/watchlist").then(({ data }) => setWatchlist(data.items || [])).catch(() => {});
  };
  useEffect(loadWatchlist, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTicker = (item) => {
    if (item.in_leaderboard) setActive(item.ticker);
    else navigate(`/search?q=${item.ticker}`);
  };

  const load = () => {
    setLoading(true);
    api.get("/scores/latest").then(({ data }) => {
      setData(data);
      if (data.weights) {
        setW({
          m: Math.round(data.weights.momentum * 100),
          t: Math.round(data.weights.ma_trend * 100),
          r: Math.round(data.weights.rsi * 100),
        });
      }
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const isCustom = data && (w.m !== Math.round(data.weights.momentum * 100)
    || w.t !== Math.round(data.weights.ma_trend * 100)
    || w.r !== Math.round(data.weights.rsi * 100));

  const rows = useMemo(() => {
    if (!data) return [];
    const sum = w.m + w.t + w.r || 1;
    const list = data.leaderboard.map((r) => {
      const recomputed = Math.max(1, Math.min(100, Math.round(
        (w.m * r.momentum_score + w.t * r.ma_trend_score + w.r * r.rsi_score) / sum
      )));
      return { ...r, displayScore: isCustom ? recomputed : r.score };
    });
    list.sort((a, b) => b.displayScore - a.displayScore);
    return list.map((r, i) => ({ ...r, displayRank: i + 1 }));
  }, [data, w, isCustom]);

  const resetWeights = () => setW({
    m: Math.round(data.weights.momentum * 100),
    t: Math.round(data.weights.ma_trend * 100),
    r: Math.round(data.weights.rsi * 100),
  });

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-96 w-full rounded-2xl" />
      </div>
    );
  }
  if (!data) return <div className="text-center py-20 text-muted-foreground">No scores yet.</div>;

  const top = rows[0];
  const avg = Math.round(rows.reduce((a, r) => a + r.displayScore, 0) / rows.length);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight">Daily Leaderboard</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
            <Calendar className="h-3.5 w-3.5" /> As of {data.as_of} · <SourceBadge source={data.source} />
          </p>
        </div>
        <Button data-testid="weights-panel-toggle" variant="outline" size="sm"
          onClick={() => setShowWeights((s) => !s)}
          className={cn("gap-1.5", isCustom && "border-primary/40 text-primary")}>
          <SlidersHorizontal className="h-3.5 w-3.5" /> Tune weights
          {isCustom && <span data-testid="weights-custom-badge" className="ml-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-primary/15 text-primary">CUSTOM</span>}
        </Button>
      </div>

      {/* Weight sliders */}
      {showWeights && (
        <div className="rounded-2xl border border-border bg-card p-6 ss-fade-up" data-testid="weights-panel">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              Adjust the blend — the leaderboard re-ranks instantly
            </p>
            <Button data-testid="weights-reset-btn" variant="ghost" size="sm" onClick={resetWeights}
              disabled={!isCustom} className="gap-1.5 text-muted-foreground">
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </Button>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            <WeightRow label="Momentum" testId="weight-slider-momentum" tint="#10B981" value={w.m} onChange={(v) => setW((p) => ({ ...p, m: v }))} />
            <WeightRow label="MA Trend" testId="weight-slider-matrend" tint="#0EA5E9" value={w.t} onChange={(v) => setW((p) => ({ ...p, t: v }))} />
            <WeightRow label="RSI Guardrail" testId="weight-slider-rsi" tint="#6366F1" value={w.r} onChange={(v) => setW((p) => ({ ...p, r: v }))} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-4">
            Scores are re-blended from each stock's stored sub-scores and normalized — no server call needed.
            {isCustom ? " Showing your custom ranking." : " Showing the official model weights."}
          </p>
        </div>
      )}

      {/* Hero metric banner */}
      <div className="col-span-full grid grid-cols-2 md:grid-cols-4 gap-4 bg-gradient-to-r from-emerald-500/10 via-sky-500/5 to-indigo-500/10 border border-emerald-500/20 p-6 rounded-2xl">
        <HeroStat icon={Trophy} label="Top Pick" value={top.ticker} sub={`Score ${top.displayScore} · ${top.company}`} tint="text-primary" />
        <HeroStat icon={Target} label="Beat-S&P Hit Rate" value={`${data.rolling_hit_rate}%`} sub={`${data.total_evaluated} picks graded`} tint="text-sky-500" />
        <HeroStat icon={Activity} label="Stocks Scored" value={rows.length} sub="Curated watchlist" tint="text-indigo-400" />
        <HeroStat icon={TrendingUp} label="Avg Score" value={avg} sub="Across watchlist" tint="text-amber-500" />
      </div>

      {/* My Watchlist */}
      {user && watchlist.length > 0 && (
        <div data-testid="my-watchlist">
          <div className="flex items-center gap-2 mb-3">
            <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
            <h2 className="text-lg font-heading font-semibold">My Watchlist</h2>
            <span className="text-xs text-muted-foreground">· pinned from AI search</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {watchlist.map((it) => (
              <button key={it.ticker} data-testid={`watchlist-item-${it.ticker}`} onClick={() => openTicker(it)}
                className="text-left rounded-xl border border-border bg-card p-4 hover:border-amber-500/40 hover:-translate-y-0.5 transition-all active:scale-[0.99]">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-bold truncate">{it.ticker}</span>
                  {it.score != null ? <ScoreBadge score={it.score} /> : <span className="text-xs text-muted-foreground">tap</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate mt-1">{it.company}</div>
                <div className="flex items-center gap-2 mt-2">
                  {it.risk_level && <RiskBadge level={it.risk_level} />}
                  {it.momentum_pct != null && (
                    <span className={cn("text-xs font-mono", it.momentum_pct >= 0 ? "text-emerald-500" : "text-red-500")}>
                      {it.momentum_pct >= 0 ? "▲" : "▼"} {Math.abs(it.momentum_pct)}%
                    </span>
                  )}
                  {!it.in_leaderboard && <span className="text-[10px] text-muted-foreground">custom</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden md:block rounded-2xl border border-border bg-card overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm" data-testid="leaderboard-table">
          <thead>
            <tr className="text-left text-xs font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
              <th className="px-5 py-3 font-medium">#</th>
              <th className="px-5 py-3 font-medium">Ticker</th>
              <th className="px-5 py-3 font-medium">Score</th>
              <th className="px-5 py-3 font-medium">Sector</th>
              <th className="px-5 py-3 font-medium">20d Momentum</th>
              <th className="px-5 py-3 font-medium">RSI</th>
              <th className="px-5 py-3 font-medium">Risk</th>
              <th className="px-5 py-3 font-medium text-right">Price</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.ticker} data-testid={`leaderboard-row-${r.ticker}`}
                onClick={() => setActive(r.ticker)}
                className="border-b border-border/60 last:border-0 hover:bg-secondary/50 cursor-pointer transition-colors">
                <td className="px-5 py-3 font-mono text-muted-foreground">{r.displayRank}</td>
                <td className="px-5 py-3">
                  <div className="font-mono font-bold">{r.ticker}</div>
                  <div className="text-xs text-muted-foreground">{r.company}</div>
                </td>
                <td className="px-5 py-3"><ScoreBadge score={r.displayScore} /></td>
                <td className="px-5 py-3 text-xs text-muted-foreground">{r.sector}</td>
                <td className={cn("px-5 py-3 font-mono font-medium",
                  r.momentum_pct >= 0 ? "text-emerald-500" : "text-red-500")}>
                  <span className="flex items-center gap-1">
                    {r.momentum_pct >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                    {r.momentum_pct}%
                  </span>
                </td>
                <td className="px-5 py-3 font-mono text-muted-foreground">{r.rsi_value}</td>
                <td className="px-5 py-3"><RiskBadge level={r.risk_level} /></td>
                <td className="px-5 py-3 font-mono text-right">${r.price}</td>
                <td className="px-5 py-3 text-right"><ChevronRight className="h-4 w-4 text-muted-foreground inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {rows.map((r) => (
          <button key={r.ticker} data-testid={`leaderboard-card-${r.ticker}`} onClick={() => setActive(r.ticker)}
            className="w-full text-left rounded-xl border border-border bg-card p-4 flex items-center gap-3 active:scale-[0.99] transition-transform">
            <span className="font-mono text-sm text-muted-foreground w-6">{r.displayRank}</span>
            <div className="flex-1 min-w-0">
              <div className="font-mono font-bold">{r.ticker}</div>
              <div className="text-xs text-muted-foreground truncate">{r.company} · {r.sector}</div>
              <div className="flex items-center gap-2 mt-1.5">
                <RiskBadge level={r.risk_level} />
                <span className={cn("text-xs font-mono", r.momentum_pct >= 0 ? "text-emerald-500" : "text-red-500")}>
                  {r.momentum_pct >= 0 ? "▲" : "▼"} {Math.abs(r.momentum_pct)}%
                </span>
              </div>
            </div>
            <ScoreBadge score={r.displayScore} />
          </button>
        ))}
      </div>

      <WhyThisPickDialog ticker={active} onClose={() => setActive(null)} onTraded={() => {}} />
    </div>
  );
}
